import { rootApi } from '../rootApi';

export const dealerApi = rootApi.injectEndpoints({
    endpoints: (builder) => ({
        getAllDealers: builder.query({
            query: (params) => {
                // Base parameters
                const queryParams = {
                    PageNumber: params.PageNumber !== undefined ? params.PageNumber : 0,
                    PageSize: params.PageSize || 10
                };

                // Only add IsActive if it has a value
                if (params.IsActive !== undefined && params.IsActive !== null) {
                    queryParams.IsActive = params.IsActive;
                }

                // Only add ShortName if it has a value
                if (params.ShortName) {
                    queryParams.ShortName = params.ShortName;
                }

                // Only add FullName if it has a value
                if (params.FullName) {
                    queryParams.FullName = params.FullName;
                }

                // Only add DealerName if it has a value
                if (params.DealerName) {
                    queryParams.DealerName = params.DealerName;
                }

                // Only add ContractStartDate if it has a value
                if (params.ContractStartDate) {
                    queryParams.ContractStartDate = params.ContractStartDate;
                }

                // Only add ContractEndDate if it has a value
                if (params.ContractEndDate) {
                    queryParams.ContractEndDate = params.ContractEndDate;
                }
                if (params.Country) {
                    queryParams.Country = params.Country;
                }

                return {
                    url: 'v1/dealer',
                    params: queryParams
                };
            },
            transformResponse: (response) => {
                // Check if response has the expected structure with data property
                if (response && typeof response === 'object') {
                    // New structure: { data: { items: [...], totalCount: number } }
                    if (response.data && response.data.items && Array.isArray(response.data.items)) {
                        return {
                            items: response.data.items,
                            totalCount: response.data.totalCount
                        };
                    }

                    // Old structure: { data: [...] }
                    if (response.data && Array.isArray(response.data)) {
                        return {
                            items: response.data,
                            totalCount: response.data.length
                        };
                    }

                    // If response itself is an array, return it directly
                    if (Array.isArray(response)) {
                        return {
                            items: response,
                            totalCount: response.length
                        };
                    }
                }

                // Fallback: return empty array if structure doesn't match
                console.warn('⚠️ Response structure not as expected, returning empty array');
                return {
                    items: [],
                    totalCount: 0
                };
            },
            // Keep track of API state for debugging
            onQueryStarted: async (arg, { queryFulfilled }) => {
                try {
                    const result = await queryFulfilled;
                } catch (error) {
                    console.error('❌ getAllDealers query failed:', error);
                }
            },
            providesTags: ['Dealer']
        }),

        getDealerById: builder.query({
            query: (id) => `v1/dealer/${id}`,
            transformResponse: (response) => {
                if (response && typeof response === 'object' && response.data) {
                    return response.data;
                }
                return null;
            },
            providesTags: (result, error, id) => [{ type: 'Dealer', id }]
        }),

        createDealer: builder.mutation({
            query: (data) => ({
                url: 'v1/dealer',
                method: 'POST',
                body: data
            }),
            invalidatesTags: ['Dealer']
        }),

        updateDealer: builder.mutation({
            query: (data) => ({
                url: 'v1/dealer',
                method: 'PATCH',
                body: data
            }),
            invalidatesTags: (result, error, { id }) => [{ type: 'Dealer', id }]
        }),

        deleteDealer: builder.mutation({
            query: (id) => ({
                url: `v1/dealer`,
                method: 'DELETE',
                body: { id }
            }),
            invalidatesTags: ['Dealer']
        }),

        getDealerDropdownList: builder.query({
            query: (params) => {
                // Base parameters
                const queryParams = {
                    RowCount: params?.RowCount || 100,
                    SearchText: params?.SearchText || ''
                };
                if (params?.DomesticOrForeign != null) {
                    queryParams.DomesticOrForeign = params.DomesticOrForeign;
                }
                return {
                    url: 'v1/dealer/GetDealerDropdownList',
                    params: queryParams
                };
            },
            transformResponse: (response) => {
                // Check if response has the expected structure with data property
                if (response && typeof response === 'object') {
                    if (response.data && Array.isArray(response.data)) {
                        return response.data;
                    }

                    // If response itself is an array, return it directly
                    if (Array.isArray(response)) {
                        return response;
                    }
                }

                // Fallback: return empty array if structure doesn't match
                console.warn('⚠️ Response structure not as expected, returning empty array');
                return [];
            },
            // Keep track of API state for debugging
            onQueryStarted: async (arg, { queryFulfilled }) => {
                try {
                    const result = await queryFulfilled;
                } catch (error) {
                    console.error('❌ getDealerDropdownList query failed:', error);
                }
            },
            providesTags: ['Dealer']
        }),
        getSalesDealerDropdownList: builder.query({
            query: (params) => {
                const queryParams = {
                    RowCount: params?.RowCount || 100,
                    SearchText: params?.SearchText || ''
                };
                if (params?.DomesticOrForeign != null) {
                    queryParams.DomesticOrForeign = params.DomesticOrForeign;
                }
                return {
                    url: 'v1/dealer/GetSalesDealerDropdownList',
                    params: queryParams
                };
            },
            transformResponse: (response) => {
                if (response && typeof response === 'object') {
                    if (response.data && Array.isArray(response.data)) {
                        return response.data;
                    }
                    if (Array.isArray(response)) {
                        return response;
                    }
                }
                console.warn('⚠️ Response structure not as expected, returning empty array');
                return [];
            },
            providesTags: ['Dealer']
        }),
        getDealerCodeDropdownList: builder.query({
            query: (params) => ({
                url: 'v1/dealer/getdealercodedropdownlist',
                params: {
                    RowCount: params?.RowCount || 100,
                    SearchText: params?.SearchText || ''
                }
            }),
            transformResponse: (response) => {
                if (response?.data && Array.isArray(response.data)) {
                    return response.data;
                }
                if (Array.isArray(response)) {
                    return response;
                }
                return [];
            },
            providesTags: ['Dealer']
        }),
        getDealerBySapDropdownList: builder.query({
            query: (params) => {
                // Base parameters
                const queryParams = {
                    RowCount: 100,
                    SearchText: params?.SearchText || ''
                };

                return {
                    url: 'v1/dealer/GetDealerBySapDropdownList',
                    params: queryParams
                };
            },
            transformResponse: (response) => {
                // Check if response has the expected structure with data property
                if (response && typeof response === 'object') {
                    if (response.data && Array.isArray(response.data)) {
                        return response.data;
                    }

                    // If response itself is an array, return it directly
                    if (Array.isArray(response)) {
                        return response;
                    }
                }
                return [];
            },
            // Keep track of API state for debugging
            onQueryStarted: async (arg, { queryFulfilled }) => {
                try {
                    const result = await queryFulfilled;
                } catch (error) {
                    console.error('❌ getDealerDropdownList query failed:', error);
                }
            },
            providesTags: ['GetDealerBySapDropdownList']
        }),
        exportDealersToExcel: builder.query({
            query: (params = {}) => ({
                url: 'v1/dealer/exporttoexcel',
                method: 'GET',
                params
            }),
            transformResponse: (response) => {
                if (response?.isSuccessful && response?.data) {
                    const data = response.data;

                    return {
                        fileBase64: data.fileBase64,
                        fileName: data.fileName,
                        contentType: data.contentType,
                        recordCount: data.recordCount,
                        fileSize: data.fileSize,
                        isCompressed: data.isCompressed
                    };
                }
                throw new Error(response?.errorMessage);
            }
        })
    })
});

export const {
    useGetAllDealersQuery,
    useGetDealerByIdQuery,
    useCreateDealerMutation,
    useUpdateDealerMutation,
    useDeleteDealerMutation,
    useGetDealerDropdownListQuery,
    useGetSalesDealerDropdownListQuery,
    useGetDealerCodeDropdownListQuery,
    useGetDealerBySapDropdownListQuery,
    useLazyExportDealersToExcelQuery
} = dealerApi;
